import {
  isVendorTaxonomyKey,
  VendorTaxonomyKey,
  SearchableVendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { V4StorefrontSearchRequest } from '@zola/svc-marketplace-ts-types';

import { LocationOrAllMarketsType } from '~/types/responseTypes';

import {
  SortValue,
  SortDirections,
  SortKeys,
  ValidSortingOptions,
  VendorSearchSorting,
} from '../types/types';

const sortKeys: SortKeys[] = ['PUBLISHED_AT', 'DISTANCE', 'CAPACITY', 'REVIEW_SCORE_SIZE'];

const sortDirections: SortDirections[] = ['ASC', 'DESC'];

export const isSortKey = (str: unknown): str is SortKeys =>
  typeof str === 'string' && sortKeys.some((key) => key === str);

export const isSortDirection = (str: unknown): str is SortDirections =>
  typeof str === 'string' && sortDirections.some((key) => key === str);

export function sortValueToString(option: VendorSearchSorting): SortValue {
  return `${option.sortBy}|${option.sortOrder}`;
}

type SortOptionFcn = (
  searchName?: V4StorefrontSearchRequest.SearchNameEnum | null
) => ValidSortingOptions[];

/**
 * Default sorting options: all storefronts can sort by these options
 */
const defaultValidSortingOptions: SortOptionFcn = (searchName) => {
  const defaultOptions: ValidSortingOptions[] = [
    { label: 'Newest', sortBy: 'PUBLISHED_AT', sortOrder: 'DESC', trackingType: 'PUBLISHED_AT' },
    { label: 'Oldest', sortBy: 'PUBLISHED_AT', sortOrder: 'ASC', trackingType: 'PUBLISHED_AT' },
    {
      label: 'Most recommended',
      sortBy: 'REVIEW_SCORE_SIZE',
      sortOrder: 'DESC',
      trackingType: 'REVIEW_SCORE_SIZE',
    },
  ];

  if (searchName === 'PRE_AUTH_SRP') {
    return [{ label: 'Distance', sortBy: null, trackingType: 'SORT' }, ...defaultOptions];
  } else {
    return [{ label: 'Best match', sortBy: null, trackingType: 'SORT' }, ...defaultOptions];
  }
};

/**
 * Venues can sort by all the defaults, plus these options
 */
const venueDefaultSortingOptions: SortOptionFcn = (searchName) =>
  defaultValidSortingOptions(searchName).concat([
    {
      label: 'Capacity: Small to big',
      sortBy: 'CAPACITY',
      sortOrder: 'ASC',
      trackingType: 'CAPACITY',
    },
    {
      label: 'Capacity: Big to small',
      sortBy: 'CAPACITY',
      sortOrder: 'DESC',
      trackingType: 'CAPACITY',
    },
  ]);

/**
 * When we have a location in our search, we can also sort venues by their distance to that
 * location.
 *
 * Warning: location may not mean what you think it means.  It a point, somewhere, probably
 * the geographic center of the city, not necessarily what the couple is thinking of.
 */
const venueWithLocationSortingOptions: SortOptionFcn = (searchName) => {
  const defaultOptions = venueDefaultSortingOptions(searchName);

  if (searchName !== 'PRE_AUTH_SRP') {
    return defaultOptions.concat([
      {
        label: 'Distance from location',
        sortBy: 'DISTANCE',
        sortOrder: 'DESC',
        trackingType: 'DISTANCE',
      },
    ]);
  }
  return defaultOptions;
};

/** Returns the display label from a sorting value */
export const getSortingLabel = (
  searchName: V4StorefrontSearchRequest.SearchNameEnum | undefined | null,
  sortValue: SortValue | null | undefined
) => {
  const options = venueWithLocationSortingOptions(searchName);

  return (
    options
      .map((option) => {
        return {
          value: option.sortBy ? sortValueToString(option) : 'DEFAULT',
          label: option.label,
        };
      })
      .find((option) => option.value === sortValue)?.label || options[0].label
  );
};

/**
 * Provides the sort options for a Search Results page
 *
 * @param taxonomyKey
 * @param selectedCity
 * @returns
 */
export const sortingByVendorType = (
  taxonomyKey?: VendorTaxonomyKey,
  selectedLocation?: LocationOrAllMarketsType | null,
  searchName?: V4StorefrontSearchRequest.SearchNameEnum | null
) =>
  getVendorTaxonomySwitchFunc<SearchableVendorTaxonomyKey | 'all', SortOptionFcn>({
    'wedding-venues': selectedLocation?.geoCoordinates?.latitude
      ? venueWithLocationSortingOptions
      : venueDefaultSortingOptions,
    'wedding-bands-djs': defaultValidSortingOptions,
    'wedding-bar-services': defaultValidSortingOptions,
    'wedding-cakes-desserts': defaultValidSortingOptions,
    'wedding-catering': defaultValidSortingOptions,
    'wedding-florists': defaultValidSortingOptions,
    'wedding-hair-makeup': defaultValidSortingOptions,
    'wedding-photographers': defaultValidSortingOptions,
    'wedding-planners': defaultValidSortingOptions,
    'wedding-videographers': defaultValidSortingOptions,
    all: defaultValidSortingOptions,
  })(isVendorTaxonomyKey(taxonomyKey) ? taxonomyKey : 'all')(searchName);
